import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	CCButton,
	CCField,
	CCModal,
	CCSpinner,
	CCSwitch,
	useToast,
} from "@pymblecitizencoders/cc-ui";
/* The house loading animation, as the portal's notice list uses. pymble-ui is
   already a dependency here for theme.css, and the dotlottie player it renders
   through is already in the bundle via cc-ui, so this costs no new payload of
   consequence. */
import { TypedLottieSpinner } from "pymble-ui/components";
import {
	CalendarCheck,
	Check,
	ChevronRight,
	SquarePen,
	Undo2,
	X,
} from "lucide-react";
import { CCAvatar } from "@pymblecitizencoders/cc-ui";
import { useProfileImages } from "../hooks/useProfileImages";
import {
	PublishQueueRow,
	PublishStatus,
	PublishStep,
	refusalDetail,
	useEventPermissions,
	publishFeedback,
	publishStatusLabel,
	usePublishList,
	usePublishRoles,
	usePublishQueue,
	usePublishWorkflow,
} from "../hooks/useEventPublish";
import { useMyDetails } from "../hooks/useMyDetails";
import { PublishChain } from "../components/eventManager/PublishChain";
import { Tooltip } from "../components/common/Tooltip";
import { renderTitle, titleText } from "../utils/eventTitle";
import { knownName, personName } from "../utils/personName";
import { CalendarEvent } from "../types/event";
import { formatWhen, whenLines } from "../utils/eventWhen";
import { decodeEntities } from "../utils/htmlEntities";
import "../components/eventManager/eventManager.css";

/**
 * Publishing sign-off — the queue colleagues release events from.
 *
 * Follows pymble-swipe's SuperAdminEvent/EventList: tabs across the top, a
 * paged list on the left, and a detail panel on the right holding the selected
 * event's chain and the decision buttons.
 *
 * NOT the carer approvals shown on the attendee roll. Those are permission
 * slips for a child to attend; this is colleagues signing an event off so
 * carers and attendees can see it at all. The API calls it publishing and so
 * does this screen, deliberately.
 *
 */

/** What the API calls each state. There is no "draft": an event that was never
    submitted simply has no workflow. `none` is this screen's word for that. */
/**
 * The mockup's tabs, in its order and with its words.
 *
 * They are not the API's statuses, so each maps onto one:
 *
 *   Pending           -> in_review
 *   Requires decision -> the queue: the steps waiting on YOU
 *   Approved          -> published
 *   Declined          -> declined
 *   All events        -> no filter at all
 *
 * "Requires decision" means something narrower in the screen this follows —
 * every gate answered, only the final sign-off left — which needs a status this
 * API does not have. Whose decision is required is the question an approver
 * actually opens this screen with, so it is wired to the queue.
 *
 * Two states have no tab of their own as a result: changes_requested and events
 * never submitted. Both appear under All events, and an event is submitted from
 * the editor's Sign-off tab rather than from here.
 */
type TabKey =
	"in_review" | "requires_decision" | "published" | "declined" | "all";

/* Row pills use the mockup's words too, so a row and its tab agree. */
const LABEL: Record<string, string> = {
	in_review: "Pending",
	changes_requested: "Changes requested",
	declined: "Declined",
	published: "Approved",
	none: "Not submitted",
};

/**
 * The same statuses, for the list's status column.
 *
 * "Changes requested" does not fit a 118px column and ran out over the chevron
 * beside it. The column is narrow on purpose — the width belongs to the event
 * title — so the word gives way instead, matching what a chain step already
 * calls the same decision. The full wording survives on the tab and in the
 * panel, where there is room for it.
 */
const ROW_LABEL: Record<string, string> = {
	...LABEL,
	changes_requested: "Changes",
};

const TABS: { key: TabKey; label: string }[] = [
	{ key: "in_review", label: "Pending" },
	{ key: "requires_decision", label: "Requires decision" },
	{ key: "published", label: "Approved" },
	{ key: "declined", label: "Declined" },
	{ key: "all", label: "All events" },
];

/** Rows fetched per page, from the server rather than sliced locally. */
const PAGE = 15;

/**
 * How deep the restricted view scans for the caller's own submissions.
 *
 * A non-administrator sees only what they submitted, and the admin list has no
 * parameter for that — so the filtering happens on what came back, and a page
 * of 15 would leave someone's events scattered across pages that each show two
 * or three. One deeper read per tab instead, paged locally.
 *
 * Bounded rather than unbounded because it is still a PII-bearing list, and 100
 * events of one person's own making, from today forward, is a long way past
 * what anyone has in flight. The footer says so when the scan fills up.
 */
const RESTRICTED_SCAN = 100;

/** The API refuses a decline or a change request with a shorter reason. */
const MIN_REASON = 10;

/* The end carries its date even when it is the same day as the start. It reads
   as a repetition on a one-day event, but a column where some rows show a date
   and others show a bare time is one the eye has to check rather than scan,
   and "16:45" alone is ambiguous the moment a multi-day event sits above it. */
/** What a row needs, whichever endpoint it came from. */
interface Row {
	id: string;
	title: string;
	start?: string;
	end?: string;
	location: string;
	status: string;
	waitingOn?: string;
	/** Present only for rows from the admin list, which returns whole events.
	    The queue returns summaries, so those rows have no chain to draw. */
	steps?: PublishStep[];
	currentStep?: number;
	submittedBy?: string;
	/** The submitter's ContactPersonal id — what "mine" is decided on. */
	submittedById?: string;
	/** What the status is CALLED — see `publishStatusLabel`. */
	statusLabel?: string;
	event?: CalendarEvent;
}

/**
 * What the title's hover card says beyond the title itself.
 *
 * Built from the row rather than the event: the queue endpoint returns
 * summaries, not whole documents, so a description is only there for rows that
 * came from the admin list.
 */
const EventTip: React.FC<{ row: Row }> = ({ row }) => {
	const about = row.event?.Description?.Short?.trim();
	const when = formatWhen(row.start);
	const ends = formatWhen(row.end);
	return (
		<>
			{when && (
				<span>
					{when}
					{ends && ends !== when ? ` to ${ends}` : ""}
				</span>
			)}
			{row.location && <span>{row.location}</span>}
			{row.waitingOn && <span>Waiting on {row.waitingOn}</span>}
			{about && <span className="em-tip__desc">{about}</span>}
		</>
	);
};

const rowFromQueue = (r: PublishQueueRow): Row => ({
	id: r.EventId,
	/* Decoded for the same reason the calendar decodes: these titles come from
	   the same feed, and a queue of events to sign off is no place to read
	   "Rapha&#235;lle". Display only — the row is never written back. */
	title: decodeEntities(r.Title ?? ""),
	start: r.StartDateTime,
	end: r.EndDateTime,
	location: "",
	status: r.Status ?? "in_review",
	waitingOn: r.CurrentRoleName ?? undefined,
	currentStep: r.CurrentStep,
	submittedBy: personName(r.SubmittedBy?.Name),
});

const rowFromEvent = (e: CalendarEvent): Row => ({
	id: e._id,
	title: decodeEntities(e.Title ?? ""),
	start: e.StartDateTime,
	end: e.EndDateTime,
	location: decodeEntities(e.Location?.Name || e.Location?.Address || ""),
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	status: (e as any).PublishWorkflow?.Status ?? "none",
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	steps: (e as any).PublishWorkflow?.Steps,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	currentStep: (e as any).PublishWorkflow?.CurrentStep,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	submittedBy: personName((e as any).PublishWorkflow?.SubmittedBy?.Name),
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	statusLabel: publishStatusLabel((e as any).PublishWorkflow?.Status),
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	submittedById: (e as any).PublishWorkflow?.SubmittedBy?._id
		? // eslint-disable-next-line @typescript-eslint/no-explicit-any
			String((e as any).PublishWorkflow.SubmittedBy._id)
		: undefined,
	event: e,
});

/** Approved, and how many are required, for the progress column and the tiles. */
const tally = (steps: PublishStep[] | undefined) => {
	const list = steps ?? [];
	const approved = list.filter((s) => s.Decision === "approved").length;
	const declined = list.filter(
		(s) => s.Decision === "declined" || s.Decision === "changes",
	).length;
	return {
		approved,
		declined,
		pending: list.length - approved - declined,
		total: list.length,
	};
};

/**
 * The page heading, shared by the loading and loaded states.
 *
 * One component rather than the same markup written twice: the two copies had
 * already drifted once, a caption change landing on the loading state alone.
 */
const Heading: React.FC<{
	onBack?: () => void;
	onRoles?: () => void;
	/** Whether to offer role administration at all — `event_publish_admin`. */
	canAdminRoles?: boolean;
	/** Softens the caption when the screen is showing one person their own. */
	restricted?: boolean;
}> = ({ onBack, onRoles, canAdminRoles, restricted }) => (
	<div className="em-req-head">
		<div>
			<h6 className="em-section-heading">Event admin</h6>
			<p className="em-muted">
				{restricted
					? "Events you have submitted, and anything waiting on you. Events move through their approvers before carers and attendees can see them."
					: "Events move through their approvers before carers and attendees can see them."}
			</p>
		</div>
		{onBack && (
			<span className="em-modal-footer__right">
				{/* Who signs off is administered here rather than buried in a
				    settings screen: the person unblocking a stalled chain is the
				    person looking at this page. Only for the people the API will
				    actually let save it. */}
				{canAdminRoles && (
					<CCButton variant="outline" size="sm" onClick={onRoles}>
						Approval admin
					</CCButton>
				)}
				<CCButton variant="outline" size="sm" onClick={onBack}>
					Back to calendar
				</CCButton>
			</span>
		)}
	</div>
);

/**
 * A value that settles before it is used.
 *
 * The search now goes to the server, so without this every keystroke is a
 * request.
 */
const useSettled = <T,>(value: T, ms = 300): T => {
	const [settled, setSettled] = useState(value);
	useEffect(() => {
		const t = setTimeout(() => setSettled(value), ms);
		return () => clearTimeout(t);
	}, [value, ms]);
	return settled;
};

export const EventApprovals: React.FC = () => {
	const navigate = useNavigate();
	const toast = useToast();
	/* Who may pull a released event back. The API allows `event_publisher` or
	   `event_admin` — not a step's role holder, since a withdrawal reaches past
	   the chain rather than moving through it. Mirrored here to decide whether
	   to OFFER the button; the API enforces it either way. */

	/* All events is offered to everyone, as the mockup has it. Nothing is
	   exposed by doing so: the admin list is readable by any active staff
	   member, and a super admin's extra power is deciding steps and assigning
	   roles, not seeing the list. */
	const [tab, setTab] = useState<TabKey>("in_review");
	const [search, setSearch] = useState("");
	const [showPast, setShowPast] = useState(false);
	const [unapprovedOnly, setUnapprovedOnly] = useState(false);
	const [page, setPage] = useState(1);
	const [selectedId, setSelectedId] = useState<string | null>(null);
	/** Which decision modal is open. */
	const [asking, setAsking] = useState<
		"approved" | "declined" | "changes" | "withdraw" | null
	>(null);
	const [reason, setReason] = useState("");
	const [reasonError, setReasonError] = useState("");

	/* Two sources. "Waiting on me" is the server's own queue, which knows which
	   roles the caller holds; every other tab is the admin list filtered by
	   status. Only one of them runs at a time. */
	const onQueue = tab === "requires_decision";
	/* Flags from a query of their own, not from whichever list a tab happens to
	   fetch — the queue tab returns none, and the calendar's query returns none
	   unless a category filter is passed. */
	const { permissions, permissionsLoading } = useEventPermissions();
	const canEdit = permissions?.event_admin === true;
	/* Withdrawal is `event_admin` or `event_publisher` — the API's rule, mirrored
	   only to decide whether to offer the button. */
	const canWithdraw = canEdit || permissions?.event_publisher === true;
	/* Who signs off is a separate authority again, and the API refuses the
	   holder endpoints without this flag. */
	const canAdminRoles = permissions?.event_publish_admin === true;

	/* Who sees the whole school's sign-off, rather than their own submissions.
	   
	   `event_publisher` belongs here as much as `event_admin` does: the API lets
	   it decide ANY step and its queue is every event in review, not a subset
	   scoped by role. Restricting a publisher to their own submissions hid the
	   events they exist to act on.
	   
	   Anyone else still gets in — a role holder has to be able to reach what is
	   waiting on them — but sees only their own submissions and their own queue,
	   with the school-wide tab hidden rather than shown empty. */
	const canSeeAll =
		canEdit ||
		permissions?.event_publisher === true ||
		/* Somebody who administers the workflows needs to see them running —
		   which step an event is stuck on is the evidence for whether the
		   workflow and its role holders are right. */
		permissions?.event_publish_admin === true;
	/* Whether the caller actually holds the step the event is sitting on.
	   
	   `CanDecide` is the server's answer and it says yes to any
	   `event_publisher`, whether or not they are on the workflow — an override,
	   deliberately. Silently, though: the buttons appeared exactly as they do
	   for the person whose step it is, so a publisher was invited to decide
	   somebody else's step without being told that is what they were doing. */
	const { roles } = usePublishRoles();
	const restricted = !canSeeAll;
	const { contactId } = useMyDetails();

	const {
		rows: queueRows,
		total: queueTotal,
		queueLoading,
	} = usePublishQueue(page, PAGE, onQueue);
	const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
	const settledSearch = useSettled(search.trim());

	/**
	 * Where the list starts.
	 *
	 * Today by default: this is a queue, and an event that has already happened
	 * is not one anybody can usefully sign off.
	 *
	 * "Show past events" winds back three months rather than removing the bound
	 * altogether. Unbounded is not the same as useful here — the list is sorted
	 * oldest first over 6,316 events, so page one would be August 2025 and
	 * anything recent would be hundreds of pages in.
	 *
	 * A search drops the bound entirely, which is the way to reach something
	 * older than that. Searching is already a deliberate act of looking for one
	 * event, so the window should not be what hides it.
	 */
	const fromDate = useMemo(() => {
		if (settledSearch) return undefined;
		if (!showPast) return today;
		const back = new Date();
		back.setMonth(back.getMonth() - 3);
		return back.toISOString().slice(0, 10);
	}, [settledSearch, showPast, today]);
	/**
	 * What the list asks for.
	 *
	 * The status tabs already name one state, so the toggle only means anything
	 * on All events, where it narrows the query to everything still
	 * outstanding: in review, sent back, declined, and never submitted.
	 *
	 * Server-side, through the API's `unapproved` filter, not by hiding rows
	 * after they arrive. The list is paged, so filtering the fetched page would
	 * quietly drop matches further down it.
	 */
	const listStatus =
		tab === "all" && unapprovedOnly
			? ("unapproved" as const)
			: (tab as PublishStatus | "all");

	const { events: listEvents, listLoading } = usePublishList(
		listStatus,
		restricted ? RESTRICTED_SCAN : PAGE,
		restricted ? 1 : page,
		!onQueue,
		fromDate,
		settledSearch || undefined,
		/**
		 * All events asks for `manual` only; the status tabs ask for everything.
		 *
		 * Sign-off is for events made in Event Manager. Unfiltered, All events
		 * is 1,968 rows of synced Edumate timetable between now and next year,
		 * and a manually created event sat at position 171 — page twelve — which
		 * is indistinguishable from missing.
		 *
		 * The status tabs are deliberately NOT narrowed: they already filter on
		 * a workflow status, so they are short, and an event of any source that
		 * has been submitted must never be hidden from the queue by a source
		 * filter it knows nothing about.
		 */
		tab === "all" ? "manual" : undefined,
	);

	const matching = useMemo<Row[]>(() => {
		if (!onQueue) {
			const all = listEvents.map(rowFromEvent);
			/* Someone who does not administer events sees their own submissions
			   and nothing else.
			   
			   Filtered here because the admin list has no "submitted by me"
			   parameter to ask for — see the fetch above, which asks for a
			   deeper page in this mode so the filtering has something to work
			   with. An API filter would be the right home for this; until then
			   the rule is enforced on what came back, and the events of other
			   people are never shown even though they were fetched.
			   
			   No contact id yet means show nothing rather than everything: the
			   safe direction while the answer is still in flight. */
			if (!restricted) return all;
			return all.filter((r) => !!contactId && r.submittedById === contactId);
		}
		/* The queue endpoint takes no search, so its rows are filtered here.
		   That is sound: the queue is what is waiting on one person, which is a
		   short list, not thousands. */
		const term = settledSearch.toLowerCase();
		const all = queueRows.map(rowFromQueue);
		return term
			? all.filter((r) => titleText(r.title).toLowerCase().includes(term))
			: all;
	}, [onQueue, queueRows, listEvents, settledSearch, restricted, contactId]);

	/* The restricted list is paged here, since the server paged the scan rather
	   than the caller's own events. Everything else is already one page. */
	const rows = useMemo<Row[]>(
		() =>
			restricted && !onQueue
				? matching.slice((page - 1) * PAGE, page * PAGE)
				: matching,
		[matching, restricted, onQueue, page],
	);

	/* The scan came back full, so there may be older submissions it did not
	   reach. Said out loud rather than left as a silent truncation. */
	const scanFilled =
		restricted && !onQueue && listEvents.length >= RESTRICTED_SCAN;

	const loading = onQueue ? queueLoading : listLoading;

	const {
		workflow,
		workflowLoading,
		decide,
		deciding,
		withdraw,
		withdrawing,
		submit,
		submitting,
	} = usePublishWorkflow(selectedId);

	/* Changing tab or search reshuffles the list under the panel, so a detail
	   left open would be describing a row that is no longer there. */
	useEffect(() => {
		setPage(1);
		setSelectedId(null);
	}, [tab, search, showPast, unapprovedOnly]);

	const selected = useMemo(
		() => rows.find((r) => r.id === selectedId) ?? null,
		[rows, selectedId],
	);
	const { imageUrl } = useProfileImages();
	/* Whoever submitted it is the person a decision goes back to. The row knows
	   the name for list rows; the workflow knows it for every row. */
	/* Without the salutation: the initials CCAvatar derives come from the first
	   and last word, so "Mr Iggy Sandejas" drew MS. */
	/* Why a stopped chain stopped — History, not Steps. See publishFeedback. */
	const feedback = publishFeedback(workflow);
	const currentStep =
		typeof workflow?.CurrentStep === "number"
			? workflow.Steps[workflow.CurrentStep]
			: undefined;
	const holdsCurrentStep = useMemo(() => {
		if (!currentStep || !contactId) return false;
		const role = roles.find((r) => r.Code === currentStep.RoleId);
		return (role?.Holders ?? []).some(
			(h) =>
				h.Live && (h.ContactId === contactId || h.DelegateTo === contactId),
		);
	}, [roles, currentStep, contactId]);
	/** Deciding a step that is not yours, as publisher. Reset per selection. */
	const [overriding, setOverriding] = useState(false);
	useEffect(() => setOverriding(false), [selectedId]);
	const owner = personName(
		workflow?.SubmittedBy?.Name || selected?.submittedBy || "",
	);
	const when = whenLines(selected?.start, selected?.end);

	const ask = (decision: "approved" | "declined" | "changes" | "withdraw") => {
		setReason("");
		setReasonError("");
		setAsking(decision);
	};

	const confirm = async () => {
		if (!asking) return;
		/* The API enforces this too and would answer 422; checking here means the
		   person is told before the round trip rather than after it. */
		if (asking !== "approved" && reason.trim().length < MIN_REASON) {
			setReasonError(
				`Please give a reason of at least ${MIN_REASON} characters.`,
			);
			return;
		}
		if (asking === "withdraw") {
			await onWithdraw();
			return;
		}
		try {
			await decide({ decision: asking, reason: reason.trim() || undefined });
			setAsking(null);
			toast.show({
				message:
					asking === "approved"
						? "Approved."
						: asking === "declined"
							? "Declined."
							: "Sent back for changes.",
				severity: "success",
				duration: 3000,
			});
		} catch (error) {
			toast.show({
				message:
					refusalDetail(error) ??
					"Could not record that decision. Please try again.",
				severity: "danger",
				duration: 5000,
			});
		}
	};

	/* Its own handler rather than a fourth branch of the decision one: it calls a
	   different endpoint, and the only thing the two share is the modal. */
	const onWithdraw = async () => {
		try {
			await withdraw(reason.trim());
			setAsking(null);
			toast.show({
				message: "Withdrawn. Carers can no longer see this event.",
				severity: "success",
				duration: 4000,
			});
		} catch (error) {
			toast.show({
				message:
					refusalDetail(error) ??
					"Could not withdraw this event. Please try again.",
				severity: "danger",
				duration: 5000,
			});
		}
	};

	const onSubmit = async () => {
		try {
			await submit();
			toast.show({
				message: "Submitted for sign-off.",
				severity: "success",
				duration: 3000,
			});
		} catch (error) {
			toast.show({
				message:
					refusalDetail(error) ??
					"Could not submit this event. Please try again.",
				severity: "danger",
				duration: 6000,
			});
		}
	};

	/* Waiting on the flags, since they decide which of the two screens this is.
	   Rendering the restricted one first and widening it a moment later would
	   flash a stripped-back list at an administrator. */
	if (permissionsLoading || !permissions) {
		return (
			<div className="em-page em-page--fill" data-surface="page">
				<div className="em-container">
					<div className="em-panel" data-surface="card">
						<Heading />
						<div className="em-approve-loading">
							<TypedLottieSpinner
								type="Searching"
								size={180}
								label="Loading events..."
							/>
						</div>
					</div>
				</div>
			</div>
		);
	}

	/* No blanket denial any more. The list endpoint is readable by any active
	   staff member, and someone holding an approving role has to be able to
	   reach what is waiting on them without also administering events. What a
	   non-administrator gets instead is the restricted view: their own
	   submissions, their own queue, and none of the tabs or controls that
	   belong to the whole school. */

	return (
		<div className="em-page em-page--fill" data-surface="page">
			<div className="em-container">
				<div className="em-panel" data-surface="card">
					<Heading
						onBack={() => navigate("/home")}
						onRoles={() => navigate("/approvals/admin")}
						canAdminRoles={canAdminRoles}
						restricted={restricted}
					/>

					<div className="em-tabbar" role="tablist">
						{/* "All events" is every event in the school, and the
						    restricted view has no business offering a tab it would
						    have to strip back to a handful. The rest all narrow to
						    the caller's own. */}
						{TABS.filter((t) => !restricted || t.key !== "all").map((t) => (
							<CCButton
								key={t.key}
								variant="underline"
								role="tab"
								selected={tab === t.key}
								aria-selected={tab === t.key}
								onClick={() => setTab(t.key)}
							>
								{t.label}
								{t.key === "requires_decision" && queueTotal > 0
									? ` (${queueTotal})`
									: ""}
							</CCButton>
						))}
					</div>

					<div className="em-approve-body">
						<div className="em-approve-list">
							<div className="em-approve-toolbar">
								<CCField
									id="em-approve-search"
									label="Search"
									type="search"
									size="sm"
									fullWidth
									value={search}
									onChange={(e) => setSearch(e.target.value)}
								/>
								{/* Inert while searching, because a search already
								    ignores the date bound — leaving it live would
								    offer a switch that changes nothing. */}
								<CCSwitch
									id="em-approve-past"
									label="Show past events"
									checked={showPast}
									disabled={!!settledSearch}
									onChange={setShowPast}
								/>
								{/* Only on All events: every other tab already names one
								    state, so the switch would either do nothing or
								    contradict the tab it sits under. */}
								{tab === "all" && (
									<CCSwitch
										id="em-approve-unapproved"
										label="Not approved only"
										checked={unapprovedOnly}
										onChange={setUnapprovedOnly}
									/>
								)}
							</div>

							{loading ? (
								<div className="em-text-center em-mt-sm">
									<CCSpinner size="sm" label="Loading..." />
								</div>
							) : rows.length === 0 ? (
								<div className="em-picker-empty">
									{search
										? "No events match that search."
										: tab === "requires_decision"
											? "Nothing is waiting on your decision."
											: tab === "all"
												? "There are no events."
												: "No events here."}
								</div>
							) : (
								<>
									{/* The prototype's column set: event, date, progress,
									    status, and the chevron. The header is a real row of
									    the same grid, so the columns cannot drift apart. */}
									{/* Header and rows share one bordered box: as siblings
									    they sat in different content widths — the rows inside
									    a 1px border, the header outside it — and every
									    fr-sized column landed 2px apart. */}
									<div className="em-approve-table">
										<div className="em-approve-head" aria-hidden="true">
											<span>Event</span>
											<span>Date</span>
											<span>Progress</span>
											<span>Status</span>
											<span />
										</div>

										<div className="em-approve-rows" role="list">
											{rows.map((r) => {
												const open = r.id === selectedId;
												const t = tally(r.steps);
												return (
													<button
														key={r.id}
														type="button"
														role="listitem"
														className={
															`em-approve-row is-${r.status}` +
															(open ? " is-open" : "")
														}
														onClick={() => setSelectedId(open ? null : r.id)}
													>
														<span className="em-approve-row__main">
															<Tooltip
																label={titleText(r.title) || "Untitled event"}
																image={
																	r.event?.Banner?.ImageUrl
																		? { src: r.event.Banner.ImageUrl }
																		: undefined
																}
																details={<EventTip row={r} />}
																className="em-approve-row__title"
																align="start"
																interactive
															>
																{titleText(r.title)
																	? renderTitle(r.title)
																	: "Untitled event"}
															</Tooltip>
															<span className="em-muted">
																{r.submittedBy
																	? `Submitted by ${r.submittedBy}`
																	: r.location}
															</span>
														</span>

														<span className="em-approve-row__when">
															<span>{whenLines(r.start, r.end)[0]}</span>
															<span className="em-muted">
																{whenLines(r.start, r.end)[1]}
															</span>
														</span>

														{/* Progress needs the chain, which only the
													    list endpoint returns; the queue sends a
													    summary, so those rows say what they are
													    waiting on instead of drawing a bar from
													    numbers they do not have. */}
														<span className="em-approve-row__progress">
															{t.total > 0 ? (
																<>
																	<span className="em-muted">
																		{t.approved} of {t.total} approved
																	</span>
																	<span
																		className="em-bar"
																		role="img"
																		aria-label={`${t.approved} of ${t.total} approved`}
																	>
																		<span
																			className={`em-bar__fill is-${r.status}`}
																			style={{
																				width: `${Math.round((t.approved / t.total) * 100)}%`,
																			}}
																		/>
																	</span>
																</>
															) : (
																<span className="em-muted">
																	{r.waitingOn
																		? `Waiting on ${r.waitingOn}`
																		: "\u2014"}
																</span>
															)}
														</span>

														<span
															className={`em-approve-status is-${r.status}`}
															// The full wording, for anyone whose pointer
															// stops on the shortened one.
															title={
																r.statusLabel ?? LABEL[r.status] ?? r.status
															}
														>
															{r.statusLabel === "Changes requested"
																? ROW_LABEL[r.status]
																: (r.statusLabel ??
																	ROW_LABEL[r.status] ??
																	r.status)}
														</span>
														<ChevronRight
															size={16}
															className="em-approve-row__go"
															aria-hidden="true"
														/>
													</button>
												);
											})}
										</div>
									</div>

									<div className="em-approve-footer">
										<span className="em-muted">
											{onQueue
												? `Showing ${rows.length} of ${queueTotal} events`
												: restricted
													? /* Counted against the caller's own, not the
													     page the server returned — and honest about
													     the scan's floor when it filled up. */
														`Showing ${rows.length} of ${matching.length}${
															scanFilled ? "+" : ""
														} events you submitted`
													: `Showing ${rows.length} events`}
										</span>
										<span className="em-approve-pager">
											<button
												type="button"
												disabled={page === 1}
												onClick={() => setPage((n) => n - 1)}
												aria-label="Previous page"
											>
												&lsaquo;
											</button>
											{/* Numbered only where a total is known. The admin
											    list returns no count, so inventing page
											    numbers there would be guessing. */}
											{onQueue && queueTotal > 0 ? (
												Array.from(
													{
														length: Math.ceil(queueTotal / PAGE),
													},
													(_, i) => i + 1,
												).map((n) => (
													<button
														key={n}
														type="button"
														className={n === page ? "is-active" : ""}
														onClick={() => setPage(n)}
													>
														{n}
													</button>
												))
											) : (
												<button type="button" className="is-active">
													{page}
												</button>
											)}
											<button
												type="button"
												disabled={rows.length < PAGE}
												onClick={() => setPage((n) => n + 1)}
												aria-label="Next page"
											>
												&rsaquo;
											</button>
										</span>
									</div>
								</>
							)}
						</div>

						<aside
							className="em-approve-detail"
							data-surface="card"
							aria-label="Sign-off details"
						>
							{!selected ? (
								<div className="em-approve-blank">
									<CalendarCheck size={30} aria-hidden="true" />
									<p className="em-approve-blank__title">No event selected</p>
									<p className="em-muted">
										Choose an event to see where it sits in the approval
										workflow.
									</p>
								</div>
							) : (
								<>
									<div className="em-approve-detail__head">
										<h6 className="em-section-heading">Sign-off details</h6>
										<CCButton
											variant="outline"
											size="sm"
											onClick={() => setSelectedId(null)}
										>
											<X size={15} aria-hidden="true" />
											<span className="em-sr-only">Close</span>
										</CCButton>
									</div>

									{selected.event?.Banner?.ImageUrl && (
										<img
											className="em-approve-detail__banner"
											src={selected.event.Banner.ImageUrl}
											alt=""
											onError={(e) => {
												e.currentTarget.style.display = "none";
											}}
										/>
									)}

									{/* Owner, event, when, where — the four things someone
									    checks before deciding, in the order the example
									    puts them. The photo is the submitter's: the person
									    to go back to if the answer is no. */}
									<div className="em-approve-ident">
										<CCAvatar
											src={imageUrl(
												workflow?.SubmittedBy?._id
													? String(workflow.SubmittedBy._id)
													: undefined,
											)}
											name={owner || titleText(selected.title) || "Event"}
											size={44}
											decorative
										/>
										<div>
											<div className="em-approve-detail__title">
												{titleText(selected.title)
													? renderTitle(selected.title)
													: "Untitled event"}
											</div>
											<div className="em-muted">
												{[when[0], when[1]].filter(Boolean).join(" · ")}
											</div>
											{owner && <div className="em-muted">Owner: {owner}</div>}
											{selected.location && (
												<div className="em-muted">{selected.location}</div>
											)}
										</div>
									</div>

									{/* The prototype's Approval Summary: counts across
									    THIS event's steps, which are real now that the
									    chain comes back with the workflow. */}
									{workflow && workflow.Steps.length > 0 && (
										<>
											<div className="em-approve-section">Approval summary</div>
											<div className="em-approve-summary">
												{(
													[
														[
															"published",
															"Approved",
															tally(workflow.Steps).approved,
														],
														[
															"in_review",
															"Pending",
															tally(workflow.Steps).pending,
														],
														[
															"declined",
															"Declined",
															tally(workflow.Steps).declined,
														],
													] as const
												).map(([tone, label, n]) => (
													<div
														key={label}
														className={`em-approve-tile is-${tone}`}
													>
														<div className="em-approve-tile__num">{n}</div>
														<div className="em-muted">{label}</div>
													</div>
												))}
											</div>
										</>
									)}

									<div className="em-approve-section">Approval workflow</div>
									{workflowLoading ? (
										<div className="em-text-center em-mt-sm">
											<CCSpinner size="sm" label="Loading..." />
										</div>
									) : !workflow?.Status ? (
										/* Never submitted. The endpoint answers with an
										   object either way, so the STATUS is what says
										   whether there is a workflow — checking the object
										   finds one that is not there. */
										<>
											<div className="em-picker-empty">
												This event has not been submitted for sign-off yet.
											</div>
											<CCButton
												variant="solid"
												colour="primary"
												size="sm"
												fullWidth
												className="em-mt-sm"
												disabled={submitting}
												onClick={onSubmit}
											>
												{submitting ? "Submitting..." : "Submit for sign-off"}
											</CCButton>
										</>
									) : (
										<>
											<PublishChain
												steps={workflow.Steps}
												currentStep={workflow.CurrentStep}
												inReview={workflow.Status === "in_review"}
											/>

											{/* Decline feedback, as the prototype cards it:
											    the only prose anyone has written here, so it
											    is not left buried in the step row.
											    
											    The history entry comes FIRST and appears even
											    when no step carries a reason: a re-submit
											    replaces `Steps`, and an invalidation writes no
											    step at all, so an event sitting in
											    changes_requested could show no explanation
											    whatsoever. */}
											{(feedback ||
												workflow.Steps.filter((st) => st.Reason).length >
													0) && (
												<>
													<div className="em-approve-section">
														Decision feedback
													</div>
													{feedback && (
														<div
															className={`em-approve-feedback is-${feedback.kind}`}
														>
															{/* Named whenever the stamp knows who. "Unknown"
															    is what the API writes when it cannot resolve
															    the person, and printing that as a name is
															    worse than leaving the who out. */}
															<strong>
																{(() => {
																	const who = knownName(feedback.by);
																	if (feedback.kind === "invalidated") {
																		return who
																			? `Edited after sign-off by ${who}`
																			: "Edited after sign-off";
																	}
																	const verb =
																		feedback.kind === "declined"
																			? "Declined"
																			: "Changes requested";
																	return who ? `${verb} by ${who}` : verb;
																})()}
																{feedback.at
																	? `, ${formatWhen(feedback.at)}`
																	: ""}
															</strong>
															{feedback.reason && (
																<span>{feedback.reason}</span>
															)}
														</div>
													)}
													{workflow.Steps.filter((st) => st.Reason).map(
														(st) => (
															<div
																key={`fb-${st.RoleId}-${st.Order}`}
																className="em-approve-feedback"
															>
																<strong>
																	{st.DecidedBy?.Name || st.Name || st.RoleId}
																	{st.DecidedAt
																		? `, ${formatWhen(st.DecidedAt)}`
																		: ""}
																</strong>
																<span>{st.Reason}</span>
															</div>
														),
													)}
												</>
											)}

											{workflow.SubmittedBy?.Name && (
												<p className="em-muted em-mt-sm">
													Submitted by {personName(workflow.SubmittedBy.Name)}
													{workflow.SubmittedAt
														? `, ${formatWhen(workflow.SubmittedAt)}`
														: ""}
												</p>
											)}

											{/* Only when there is something to do.
											    
											    The section used to render whatever the state was,
											    so an event nobody was waiting on you for still
											    showed "Your decision" over "This step is not yours
											    to decide" — a heading promising an action and a
											    sentence taking it away. The pill and the chain
											    already say where the event stands; a heading is
											    only earned by a button underneath it. */}
											{(workflow.CanDecide ||
												(workflow.Status === "published" && canWithdraw)) && (
												<div className="em-approve-section">Your decision</div>
											)}
											{workflow.CanDecide &&
											!holdsCurrentStep &&
											!overriding ? (
												/* Not your step. The power to decide it anyway is
												   real and worth keeping — a publisher is who
												   unblocks a chain nobody else can — but it is not
												   the default, and it says whose step it is. */
												<div className="em-approve-override">
													<p className="em-muted">
														This step is with{" "}
														<strong>
															{currentStep?.Name ?? "another approver"}
														</strong>
														, which you do not hold. You can decide it as
														publisher.
													</p>
													<CCButton
														variant="outline"
														size="sm"
														fullWidth
														onClick={() => setOverriding(true)}
													>
														Decide as publisher
													</CCButton>
												</div>
											) : workflow.CanDecide ? (
												<>
													{!holdsCurrentStep && (
														<p className="em-approve-override__note">
															Deciding <strong>{currentStep?.Name}</strong> as
															publisher, not as its holder.
														</p>
													)}
													<div className="em-approve-actions--split">
														<CCButton
															variant="solid"
															colour="success"
															size="sm"
															disabled={deciding}
															onClick={() => ask("approved")}
														>
															<Check size={15} aria-hidden="true" /> Approve
														</CCButton>
														<CCButton
															variant="solid"
															colour="danger"
															size="sm"
															disabled={deciding}
															onClick={() => ask("declined")}
														>
															<X size={15} aria-hidden="true" /> Decline
														</CCButton>
													</div>
													<CCButton
														variant="solid"
														colour="primary"
														size="sm"
														fullWidth
														className="em-mt-sm"
														disabled={deciding}
														onClick={() => ask("changes")}
													>
														<SquarePen size={15} aria-hidden="true" /> Request
														changes
													</CCButton>
												</>
											) : (
												/* CanDecide is the server's answer, so the buttons
												   never have to work out role membership themselves.
												   
												   The way back out is the only thing offered here, and
												   only from published — anything else is already
												   unreleased, and the API answers 409 for it. Outline
												   rather than solid: undoing what the whole chain
												   agreed on should not be the most prominent thing in
												   the panel. */
												<>
													{workflow.Status === "published" && canWithdraw && (
														<CCButton
															variant="outline"
															colour="danger"
															size="sm"
															fullWidth
															className="em-mt-sm"
															disabled={withdrawing}
															onClick={() => ask("withdraw")}
														>
															<Undo2 size={15} aria-hidden="true" /> Withdraw
															from the calendar
														</CCButton>
													)}
												</>
											)}
										</>
									)}

									<CCButton
										variant="outline"
										size="sm"
										fullWidth
										className="em-mt-sm"
										onClick={() => navigate(`/event/${selected.id}`)}
									>
										Open the event
									</CCButton>
								</>
							)}
						</aside>
					</div>
				</div>
			</div>

			{asking && (
				<CCModal
					open
					onClose={() => setAsking(null)}
					title={
						asking === "approved"
							? "Approve this event"
							: asking === "declined"
								? "Decline this event"
								: asking === "withdraw"
									? "Withdraw this event"
									: "Request changes"
					}
					footer={
						<div className="em-modal-footer">
							<CCButton variant="outline" onClick={() => setAsking(null)}>
								Cancel
							</CCButton>
							<CCButton
								variant="solid"
								colour={asking === "withdraw" ? "danger" : "primary"}
								disabled={deciding || withdrawing}
								onClick={confirm}
							>
								{deciding || withdrawing
									? "Saving..."
									: asking === "withdraw"
										? "Withdraw"
										: "Confirm"}
							</CCButton>
						</div>
					}
				>
					<p className="em-muted">
						{asking === "approved"
							? "This passes the event to the next approver, or publishes it if you are the last."
							: asking === "withdraw"
								? /* Spell out the three consequences. Carers may already have
								     responded to this event, and the approvals given before it
								     was released do not carry over to the next run. */
									"Carers and attendees will stop seeing this event, and it goes back to the organiser. Re-submitting starts the approval workflow again from the first approver."
								: "Say what needs to change. Whoever submitted the event sees this."}
					</p>
					<CCField
						id="em-approve-reason"
						label={asking === "approved" ? "Note (optional)" : "Reason"}
						multiline
						rows={4}
						fullWidth
						autoFocus
						error={reasonError || undefined}
						value={reason}
						onChange={(e) => {
							setReason(e.target.value);
							if (reasonError) setReasonError("");
						}}
					/>
				</CCModal>
			)}
		</div>
	);
};
